import { defineComponent as _defineComponent } from 'vue'
import { normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = ["title"]
const _hoisted_3 = ["title"]

import { ref, computed, watch, onUnmounted, nextTick } from 'vue'
import { DialogComponent as EjsDialog } from '@syncfusion/ej2-vue-popups'
import { useTheme } from '../../composables/useTheme'

// Type definitions for better type safety
interface NodeDetailsDialogProps {
  isVisible: boolean
  nodeContent: string
  nodePath: string
}

interface JsonLine {
  key: string
  value: string
  hasComma: boolean
}

interface CopyStates {
  content: boolean
  path: boolean
}

// Define emits

export default /*@__PURE__*/_defineComponent({
  __name: 'NodeDetailsDialog',
  props: {
    isVisible: { type: Boolean },
    nodeContent: {},
    nodePath: {}
  },
  emits: ["close"],
  setup(__props: any, { emit: __emit }) {

const emit = __emit

// Define props
const props = __props

// Use theme composable
const { themeSettings } = useTheme()

// Constants for better maintainability
const DIALOG_CONFIG = {
  WIDTH: '400px',
  HEADER: 'Node Details',
  CSS_CLASS: 'node-details-dialog',
  ANIMATION_EFFECT: 'Zoom' as const,
  COPY_FEEDBACK_DURATION: 1500
} as const

const COPY_BUTTON_CLASSES = {
  CONTENT: 'copy-content-btn',
  PATH: 'copy-path-btn'
} as const

const ICON_CLASSES = {
  COPY: 'e-icons e-copy',
  CHECK: 'e-icons e-check'
} as const

const DIALOG_STYLES = {
  container: { fontFamily: 'Segoe UI, sans-serif', fontSize: '14px' },
  section: { marginBottom: '15px' },
  label: { fontWeight: 500, display: 'block', marginBottom: '5px' },
  dialogBox: { borderRadius: '5px', position: 'relative' as const },
  codeContent: { 
    padding: '10px', 
    overflowX: 'auto' as const, 
    fontFamily: 'Consolas', 
    fontSize: '14px' 
  },
  jsonLine: { lineHeight: '16px' },
  jsonKey: { fontWeight: 550, marginLeft: '14px' },
  copyButton: {
    position: 'absolute' as const,
    top: '5px',
    right: '5px',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: '4px'
  },
  copyIcon: { color: '#6C757D' }
} as const

// Reactive state
const copyStates = ref<CopyStates>({ content: false, path: false })
const timeoutRefs = ref<{ content?: number; path?: number }>({})

// Parse content lines into formatted JSON key-value pairs
const formatJsonLines = (inputContent: string): JsonLine[] => {
  if (!inputContent?.trim()) return []

  return inputContent.split('\n').reduce<JsonLine[]>((acc, currentLine, lineIndex, arr) => {
    const colonIndex = currentLine.indexOf(':')
    if (colonIndex === -1) return acc

    const extractedKey = currentLine.slice(0, colonIndex).trim()
    let extractedValue = currentLine.slice(colonIndex + 1).trim()
    let processedValue: string

    // Format value based on type (boolean, number, or string)
    if (/^(true|false)$/i.test(extractedValue)) {
      processedValue = extractedValue.toLowerCase()
    } else if (!isNaN(parseFloat(extractedValue))) {
      processedValue = extractedValue
    } else {
      processedValue = `"${extractedValue.replace(/^"(.*)"$/, '$1')}"`
    }

    acc.push({
      key: `"${extractedKey}"`,
      value: processedValue,
      hasComma: lineIndex !== arr.length - 1,
    })
    return acc
  }, [])
}

// Add curly braces around root path for better display
const formatRootPath = (pathInput: string): string => {
  return pathInput.startsWith('Root') ? `{Root}${pathInput.slice(4)}` : pathInput
}

// Computed values
const jsonLines = computed(() => formatJsonLines(props.nodeContent))
const formattedPath = computed(() => formatRootPath(props.nodePath.trim()))

// Handle copy operations with visual feedback
const handleCopy = async (text: string, type: keyof CopyStates) => {
  try {
    await navigator.clipboard.writeText(text)
    
    // Clear existing timeout for this type
    if (timeoutRefs.value[type]) {
      clearTimeout(timeoutRefs.value[type])
    }
    
    // Set copy success state
    copyStates.value = { ...copyStates.value, [type]: true }

    // Reset state after delay
    timeoutRefs.value[type] = setTimeout(() => {
      copyStates.value = { ...copyStates.value, [type]: false }
      delete timeoutRefs.value[type]
    }, DIALOG_CONFIG.COPY_FEEDBACK_DURATION)
    
  } catch (error) {
    console.error('Failed to copy text to clipboard:', error)
  }
}

// Copy formatted JSON content to clipboard
const handleCopyContent = () => {
  if (!jsonLines.value.length) {
    handleCopy(`"${props.nodeContent.trim()}"`, 'content')
    return
  }

  const formattedJson = '{\n' +
    jsonLines.value.map(({ key, value, hasComma }) => 
      `    ${key}: ${value}${hasComma ? ',' : ''}`
    ).join('\n') +
    '\n}'
  
  handleCopy(formattedJson, 'content')
}

// Copy formatted path to clipboard
const handleCopyPath = () => {
  handleCopy(formattedPath.value, 'path')
}

// Handle dialog close
const handleClose = () => {
  emit('close')
}

// Reset copy states when dialog visibility changes
watch(() => props.isVisible, (newVisible) => {
  if (!newVisible) {
    nextTick(() => {
      copyStates.value = { content: false, path: false }
      Object.values(timeoutRefs.value).forEach(timeout => {
        if (timeout) clearTimeout(timeout)
      })
      timeoutRefs.value = {}
    })
  }
})

// Cleanup timeouts on unmount
onUnmounted(() => {
  Object.values(timeoutRefs.value).forEach(timeout => {
    if (timeout) clearTimeout(timeout)
  })
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(EjsDialog), {
    width: DIALOG_CONFIG.WIDTH,
    header: DIALOG_CONFIG.HEADER,
    showCloseIcon: true,
    isModal: true,
    visible: __props.isVisible,
    animationSettings: { effect: DIALOG_CONFIG.ANIMATION_EFFECT },
    closeOnEscape: true,
    cssClass: DIALOG_CONFIG.CSS_CLASS,
    target: "body",
    onClose: handleClose,
    onOverlayClick: handleClose
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", {
        style: _normalizeStyle(DIALOG_STYLES.container)
      }, [
        _createElementVNode("div", {
          style: _normalizeStyle(DIALOG_STYLES.section)
        }, [
          _createElementVNode("label", {
            style: _normalizeStyle(DIALOG_STYLES.label)
          }, "Content", 4),
          _createElementVNode("div", {
            class: "dialog-box",
            style: _normalizeStyle(DIALOG_STYLES.dialogBox)
          }, [
            _createElementVNode("div", {
              style: _normalizeStyle(DIALOG_STYLES.codeContent)
            }, [
              (jsonLines.value.length === 0)
                ? (_openBlock(), _createElementBlock("span", {
                    key: 0,
                    style: _normalizeStyle({ color: _unref(themeSettings).popupValueColor })
                  }, " \"" + _toDisplayString(__props.nodeContent.trim()) + "\" ", 5))
                : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                    _cache[1] || (_cache[1] = _createElementVNode("div", null, "{", -1)),
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(jsonLines.value, ({ key, value, hasComma }, index) => {
                      return (_openBlock(), _createElementBlock("div", {
                        key: `${key}-${index}`,
                        style: _normalizeStyle(DIALOG_STYLES.jsonLine)
                      }, [
                        _createElementVNode("span", {
                          style: _normalizeStyle({ 
                  color: _unref(themeSettings).popupKeyColor, 
                  ...DIALOG_STYLES.jsonKey
                })
                        }, _toDisplayString(key), 5),
                        _cache[0] || (_cache[0] = _createElementVNode("span", { style: { marginRight: '3px' } }, ":", -1)),
                        _createElementVNode("span", {
                          style: _normalizeStyle({ color: _unref(themeSettings).popupValueColor })
                        }, _toDisplayString(value), 5),
                        hasComma
                          ? (_openBlock(), _createElementBlock("span", _hoisted_1, ","))
                          : _createCommentVNode("", true)
                      ], 4))
                    }), 128)),
                    _cache[2] || (_cache[2] = _createElementVNode("div", null, "}", -1))
                  ], 64))
            ], 4),
            _createElementVNode("button", {
              class: _normalizeClass(COPY_BUTTON_CLASSES.CONTENT),
              type: "button",
              onClick: handleCopyContent,
              style: _normalizeStyle(DIALOG_STYLES.copyButton),
              title: copyStates.value.content ? 'Copied!' : 'Copy'
            }, [
              _createElementVNode("span", {
                class: _normalizeClass(copyStates.value.content ? ICON_CLASSES.CHECK : ICON_CLASSES.COPY),
                style: _normalizeStyle(DIALOG_STYLES.copyIcon)
              }, null, 6)
            ], 14, _hoisted_2)
          ], 4)
        ], 4),
        _createElementVNode("div", null, [
          _createElementVNode("label", {
            style: _normalizeStyle(DIALOG_STYLES.label)
          }, "JSON Path", 4),
          _createElementVNode("div", {
            class: "dialog-box",
            style: _normalizeStyle(DIALOG_STYLES.dialogBox)
          }, [
            _createElementVNode("div", {
              style: _normalizeStyle(DIALOG_STYLES.codeContent)
            }, _toDisplayString(formattedPath.value), 5),
            _createElementVNode("button", {
              class: _normalizeClass(COPY_BUTTON_CLASSES.PATH),
              type: "button",
              onClick: handleCopyPath,
              style: _normalizeStyle(DIALOG_STYLES.copyButton),
              title: copyStates.value.path ? 'Copied!' : 'Copy'
            }, [
              _createElementVNode("span", {
                class: _normalizeClass(copyStates.value.path ? ICON_CLASSES.CHECK : ICON_CLASSES.COPY),
                style: _normalizeStyle(DIALOG_STYLES.copyIcon)
              }, null, 6)
            ], 14, _hoisted_3)
          ], 4)
        ])
      ], 4)
    ]),
    _: 1
  }, 8, ["width", "header", "visible", "animationSettings", "cssClass"]))
}
}

})